/* eslint-disable max-len */
import { store } from 'src/store';
import { QORE_DISPATCH_STATE_VALUE_TO_ID } from 'src/constants/qoreDispatchStatusesAndStates';
import axiosInstances from '../utils/axiosInstances';

export const DEFAULT_DISPATCH_DETAILS_INCLUDE = 'customer,pickUpLocation,dropOffLocation,digitalDispatchProvider,digitalDispatchProviderOffer,providerOffer,quoteDetail,twoWayTowReturnLocation';
/**
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 */

/**
 * @typedef {Object} DispatchReferral
 * @property {number} referralProgramId - Referral program identifier.
 * @property {boolean} wasReferralUsed - Whether referral was accepted/used.
 * @property {?number} rejectionReasonId - Rejection reason ID when referral was not used.
 * @property {number} milesAddedByReferral - Number of miles added by referral program.
 * @property {number} milesUsedByReferral - Number of referral miles consumed.
 * @property {?number} producerCodeForBilling - Producer code used for billing when referral is used.
 * @property {string} createdBy - Email of the user that created the referral decision.
 */

class RESTDispatchService {
  /**
   * @type {Axios}
   */
  #dispatchQoreAxiosInstance;

  #roadsideExtAxiosInstance;

  constructor() {
    this.#dispatchQoreAxiosInstance = axiosInstances({
      baseURL: process.env.REACT_APP_DISPATCH_QORE_API,
      headers: {
        'Ocp-Apim-Subscription-Key': process.env.REACT_APP_NSD_API_KEY
      },
      withCredentials: true
    });

    this.#roadsideExtAxiosInstance = axiosInstances({
      baseURL: process.env.REACT_APP_ROADSIDE_EXT_API,
      headers: {
        'Ocp-Apim-Subscription-Key': process.env.REACT_APP_NSD_API_KEY
      },
      withCredentials: true
    });
  }

  /**
  * Creates a new dispatch in the QORE system by sending dispatch data to the API
  *
  * @param {Object} data - The core dispatch data object
  * @param {number} data.customerId - Customer ID associated with this dispatch
  * @param {number} data.serviceId - Service type ID for this dispatch
  * @param {number} data.vehicleId - Vehicle ID associated with this dispatch
  * @param {string} data.dropOffDistance - Distance to drop-off location (e.g., "8.1 mi")
  * @param {number} data.loadedMilesKilometers - Numeric representation of the distance
  * @param {boolean} data.isScheduled - Whether this is a scheduled dispatch
  * @param {string} [data.companyIdentifier] - Company identifier for this dispatch
  * @param {DispatchReferral} [data.dispatchReferral] - Referral decision payload when vehicle based referral was offered.
  * @param {Object} pickUpLocationInfo
  * @param {number} pickUpLocationInfo.locationId - ID of the pickup location
  * @param {string} pickUpLocationInfo.fullAddress - Complete address of pickup location
  * @param {number} pickUpLocationInfo.latitude - Latitude coordinate of pickup location
  * @param {number} pickUpLocationInfo.longitude - Longitude coordinate of pickup location
  * @param {Object} dropOffLocationInfo
  * @param {number} dropOffLocationInfo.locationId - ID of the drop-off location
  * @param {string} dropOffLocationInfo.fullAddress - Complete address of drop-off location
  * @param {number} dropOffLocationInfo.latitude - Latitude coordinate of drop-off location
  * @param {number} dropOffLocationInfo.longitude - Longitude coordinate of drop-off location
  * @param {DispatchStatusToStateMapping[]} dispatchStatusAndStateMapping - Mapping object for dispatch statuses and states
  *
  * @returns {Promise<AxiosResponse>}
  */
  createNewDispatch = (data, pickUpLocationInfo, dropOffLocationInfo, dispatchStatusAndStateMapping) => {
    const newDispatchStatusAndStateMapping = dispatchStatusAndStateMapping.find(
      (mapping) => mapping.status === 'New'
    );
    const newStateId = data?.isScheduled ? QORE_DISPATCH_STATE_VALUE_TO_ID.Scheduled : newDispatchStatusAndStateMapping.stateId;
    const newStatusId = newDispatchStatusAndStateMapping.statusId;
    const createdBy = store.getState().account?.user?.email ?? '';
    const createdByName = store.getState().account?.user?.fullName ?? '';

    const dispatch = {
      ...data,
      pickUpLocationInfo,
      dropOffLocationInfo,
      stateId: newStateId,
      statusId: newStatusId,
      createdBy,
      createdByName,
    };

    return this.#dispatchQoreAxiosInstance.post('create-new-dispatch', dispatch);
  };

  /**
  * Creates and finalizes a referral (Pinnacle RVT) dispatch in a single call.
  * Mirrors createNewDispatch's dispatch assembly, but wraps the dispatch with the
  * provider towId and POSTs to the referral finalize endpoint.
  *
  * @param {Object} data - The core dispatch data object (see createNewDispatch)
  * @param {Object} pickUpLocationInfo - Pickup location info (see createNewDispatch)
  * @param {Object} dropOffLocationInfo - Drop-off location info (see createNewDispatch)
  * @param {DispatchStatusToStateMapping[]} dispatchStatusAndStateMapping - Status/state mapping
  * @param {number} towId - Provider towId used to finalize the referral dispatch
  * @returns {Promise<AxiosResponse>}
  */
  createAndCompleteReferralDispatch = (data, pickUpLocationInfo, dropOffLocationInfo, dispatchStatusAndStateMapping, towId) => {
    const newDispatchStatusAndStateMapping = dispatchStatusAndStateMapping?.find(
      (mapping) => mapping.status === 'New'
    );
    if (!newDispatchStatusAndStateMapping) {
      throw new Error('Missing dispatch status/state mapping for \'New\'');
    }
    if (!Number.isInteger(towId)) {
      throw new Error('Invalid referral towId');
    }
    // Referrals are always immediate — never scheduled — so use the 'New' state directly.
    const newStateId = newDispatchStatusAndStateMapping.stateId;
    const newStatusId = newDispatchStatusAndStateMapping.statusId;
    const createdBy = store.getState().account?.user?.email ?? '';
    const createdByName = store.getState().account?.user?.fullName ?? '';

    const dispatch = {
      ...data,
      pickUpLocationInfo,
      dropOffLocationInfo,
      stateId: newStateId,
      statusId: newStatusId,
      createdBy,
      createdByName,
    };

    // Referral finalize endpoint wraps the dispatch with the env-stable provider towId.
    return this.#dispatchQoreAxiosInstance.post('create-and-complete-referral-dispatch', { dispatch, towId });
  };

  /**
   * Fetches the current dispatch status and state mappings from
   * [dispatch].[DispatchStatusToState] table in Qore
   * @returns {Promise<AxiosResponse>}
   */
  getDispatchStatusAndStateMapping = () => this.#dispatchQoreAxiosInstance.get('get-dispatch-status-to-state-mappings');

  /**
   * Fetches all details of a specific dispatch by its ID, defaulted to return all
   * related entities
   * Field digitalDispatchProvider is only populated with accepted provider or null,
   * digitalDispatchProviderOffer is populated with all unrejected offers
   * @param {number} dispatchId - The ID of the dispatch to fetch details for
   * @param {string} [include] - Optional query parameter to specify related entities to include in the response
   * @returns {Promise<AxiosResponse<DispatchDetails>>}
   */
  getDispatchAllDetails(dispatchId, include = DEFAULT_DISPATCH_DETAILS_INCLUDE) {
    return this.#dispatchQoreAxiosInstance.get(`get-dispatch-all-details/${dispatchId}`, {
      params: {
        include
      }
    });
  }

  /**
   * Sends a rescue link to a customer for self-dispatch initialization.
   * @param {string} customerTelephoneNumber
   * @param {string} clientName
   * @returns {Promise<AxiosResponse>}
   */
  sendRescueLink(customerTelephoneNumber, clientName) {
    return this.#roadsideExtAxiosInstance.post('dispatch-portal-self-dispatch-init', {
      customerTelephoneNumber,
      clientName,
    });
  }
}

export default RESTDispatchService;
